import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { User, Phone, Mail, Lock } from 'lucide-react'
import { profileAPI, leadAPI, analyticsAPI } from '../../services/api'
import PublicProfilePage from '../PublicProfilePage'
import NotFoundPage from '../NotFoundPage'
import toast from 'react-hot-toast'

const VisitProfileView = () => {
    const { username } = useParams()
    const navigate = useNavigate()
    const [isUnlocked, setIsUnlocked] = useState(false)
    const [profileId, setProfileId] = useState(null)
    const [formData, setFormData] = useState({ name: '', phone: '', message: '' })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isNotFound, setIsNotFound] = useState(false)

    // Check session storage on mount
    useEffect(() => {
        const unlockedProfiles = JSON.parse(sessionStorage.getItem('unlockedProfiles') || '[]')
        if (unlockedProfiles.includes(username)) {
            setIsUnlocked(true)
        }
        
        // Fetch minimal profile data to get ID (mostly for analytics/verification)
        // We actually just let PublicProfilePage handle the full fetch, 
        // but here we need ID for lead creation if we don't have it.
        // For simplicity, we assume we fetch profile first to verify it exists and get ID.
        fetchProfileId()
    }, [username])

    const fetchProfileId = async () => {
        try {
            const res = await profileAPI.getPublicProfile(username)
            if (res.data.success) {
                setProfileId(res.data.data._id)
            } else {
                setIsNotFound(true)
            }
        } catch {
             setIsNotFound(true)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!formData.name || !formData.phone || !formData.message) {
            return toast.error('All fields are required')
        }
        
        setIsSubmitting(true)
        try {
            // Clean data
            const payload = {
                name: formData.name,
                phone: formData.phone,
                message: formData.message,
                targetProfileId: profileId
            }

            console.log('Submitting Lead:', payload)

            if (!profileId) {
                throw new Error('Profile ID not loaded. Please refresh.')
            }

            await leadAPI.createLead(payload)
            
            // Unlock
            const unlocked = JSON.parse(sessionStorage.getItem('unlockedProfiles') || '[]')
            unlocked.push(username)
            sessionStorage.setItem('unlockedProfiles', JSON.stringify(unlocked))
            
            toast.success('Access Granted!')
            setIsUnlocked(true)
            
            // Track unlock event
            analyticsAPI.trackEvent({
                eventType: 'lead_capture',
                eventAction: 'submit',
                profile: profileId
            }).catch(() => {})
            
        } catch (error) {
            console.error('Lead Submission Error:', error)
            const msg = error.response?.data?.message || error.response?.data?.error || error.message || 'Something went wrong.'
            toast.error(msg)
        } finally {
            setIsSubmitting(false)
        }
    }

    if (isNotFound) {
        return <NotFoundPage />
    }

    if (isUnlocked) {
        return <PublicProfilePage />
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            >
                <div className="bg-blue-400 p-8 text-white text-center">
                    <Lock className="w-12 h-12 mx-auto mb-4 opacity-80" />
                    <h2 className="text-2xl font-bold mb-2">Unlock Profile</h2>
                    <p className="opacity-90">Please enter your details to view this business profile.</p>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Your Name *</label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input 
                                type="text"
                                value={formData.name}
                                onChange={e => setFormData({...formData, name: e.target.value})}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-transparent dark:text-white"
                                placeholder="Your Name"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone Number *</label>
                        <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input 
                                type="tel"
                                value={formData.phone}
                                onChange={e => setFormData({...formData, phone: e.target.value})}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-transparent dark:text-white"
                                placeholder="Your Phone Number"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Query / Message *</label>
                        <div className="relative">
                            <textarea 
                                value={formData.message}
                                onChange={e => setFormData({...formData, message: e.target.value})}
                                className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-transparent dark:text-white min-h-[100px] resize-none"
                                placeholder="I'm interested in your services..."
                                required
                            />
                        </div>
                    </div>

                    <button 
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-primary-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-primary-700 transition-colors shadow-lg disabled:opacity-70 disabled:cursor-wait"
                    >
                        {isSubmitting ? 'Verifying...' : 'View Profile'}
                    </button>
                    
                    <p className="text-xs text-center text-gray-500 mt-4">
                        By continuing, you verify that you are a real user.
                    </p>
                </form>
            </motion.div>
        </div>
    )
}

export default VisitProfileView
